(() => {
  const theme = window.theme || { routes: {}, strings: {} };

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const normalizeLebanesePhone = (value) => {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.startsWith('00961')) digits = digits.slice(5);
    else if (digits.startsWith('961')) digits = digits.slice(3);
    if (digits.startsWith('0')) digits = digits.slice(1);
    return /^(3\d{6}|(70|71|76|78|79|81)\d{6})$/.test(digits) ? digits : null;
  };

  const formatLebanesePhone = (digits) => {
    const local = digits.length === 7 ? `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}` : `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `+961 ${local}`;
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    const value = Number(cents) / 100;
    const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2);
      }
    });
  };

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template || '');

  const escapeHTML = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const openWhatsApp = (number, message) => {
    if (!number) return;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  };

  const selectedPack = (section) => {
    const checked = section?.querySelector('.pack__input:checked');
    const price = section?.querySelector('[data-pack-price]')?.textContent.trim() || '';
    if (checked) {
      return {
        id: checked.dataset.variant || checked.value,
        qty: Number(checked.dataset.qty) || 1,
        price: checked.dataset.price,
        title: checked.dataset.title,
        stack: checked.hasAttribute('data-stack'),
      };
    }
    const input = section?.querySelector('[data-variant-input]');
    return { id: input?.value, qty: 1, price, title: '', stack: false };
  };

  window.luma = Object.assign(window.luma || {}, { normalizeLebanesePhone, formatLebanesePhone });

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
  };

  const lockScroll = () => document.documentElement.classList.add('scroll-lock');
  const unlockScroll = () => {
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('scroll-lock');
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    dialog._trigger = trigger || null;
    lockScroll();
    dialog.addEventListener(
      'close',
      () => {
        unlockScroll();
        if (dialog._trigger && document.contains(dialog._trigger)) dialog._trigger.focus();
      },
      { once: true }
    );
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.openDialog), opener);
      return;
    }

    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      event.preventDefault();
      closer.closest('dialog')?.close();
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }

    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.focus();
    });
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-disclosure]') || !details.open) return;
      document.querySelectorAll('details[data-disclosure][open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    },
    true
  );

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((button) => button.addEventListener('click', this.onButton.bind(this)));
    }

    onButton(event) {
      event.preventDefault();
      const previous = this.input.value;
      if (event.currentTarget.name === 'plus') this.input.stepUp();
      else this.input.stepDown();
      if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-cart-drawer-toggle]');
        if (!toggle) return;
        event.preventDefault();
        this.open(toggle);
      });
    }

    open(trigger) {
      openDialog(this.dialog, trigger);
    }

    renderContents(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const doc = parseHTML(html);
      const next = doc.querySelector('#CartDrawerInner');
      const current = this.querySelector('#CartDrawerInner');
      if (next && current) current.replaceWith(next);
      const count = doc.querySelector('[data-cart-drawer-count]');
      if (count) updateCartCount(count.dataset.cartDrawerCount);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    get submitButton() {
      return this.form.querySelector('[type="submit"]');
    }

    async onSubmit(event) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      event.preventDefault();
      const button = this.submitButton;
      if (!button || button.disabled) return;

      this.showError();
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (data.status) {
          this.showError(data.description || data.message || theme.strings.cartError);
          return;
        }
        drawer.renderContents(data.sections);
        const host = this.closest('dialog');
        const trigger = host?._trigger || button;
        if (host) {
          host._trigger = null;
          host.close();
        }
        drawer.open(trigger);
      } catch (error) {
        this.showError(theme.strings.cartError);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      const text = this.errorEl.querySelector('[data-form-error-text]') || this.errorEl;
      text.textContent = message || '';
    }
  }
  customElements.define('product-form', ProductForm);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = debounce(this.onChange.bind(this), 300);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.update(remove.dataset.removeLine, 0);
      });
    }

    onChange(event) {
      const input = event.target.closest('[data-line]');
      if (input) this.update(input.dataset.line, input.value);
    }

    get sectionIds() {
      return (this.dataset.sections || '').split(',').filter(Boolean);
    }

    async update(line, quantity) {
      this.classList.add('is-loading');
      try {
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: this.sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (state.status || state.errors) {
          this.showError(state.description || state.message || state.errors || theme.strings.cartError);
          this.classList.remove('is-loading');
          return;
        }
        updateCartCount(state.item_count);
        this.renderSections(state.sections || {});
      } catch (error) {
        this.showError(theme.strings.cartError);
        this.classList.remove('is-loading');
      }
    }

    renderSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const next = doc.querySelector('#CartDrawerInner');
          const current = document.querySelector('#CartDrawerInner');
          if (next && current) current.replaceWith(next);
          return;
        }
        const next = doc.querySelector(`#shopify-section-${id}`);
        const current = document.querySelector(`#shopify-section-${id}`);
        if (next && current) current.innerHTML = next.innerHTML;
      });
    }

    showError(message) {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }
  }
  customElements.define('cart-items', CartItems);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.activeId = this.slides[0]?.dataset.mediaId;
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.thumb)));
      // 2-pack photo: shown in place of the first photo while the 2-pack is picked
      if (this.querySelector('.pdp-gallery__image--two-pack')) {
        this.onPack = (event) => this.setPack(event.detail);
        document.addEventListener('pack:change', this.onPack);
        const picked = this.closest('[data-product-section]')?.querySelector('.pack__input:checked');
        if (picked) this.setPack({ qty: Number(picked.dataset.qty) || 1, stack: picked.hasAttribute('data-stack') });
      }
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
      if (this.viewer && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.viewer, threshold: 0.6 }
        );
        this.viewer.querySelectorAll('[data-media-id]').forEach((slide) => observer.observe(slide));
      }
    }

    disconnectedCallback() {
      if (this.onPack) document.removeEventListener('pack:change', this.onPack);
    }

    setPack({ qty, stack } = {}) {
      const twoPack = !stack && Number(qty) === 2;
      if (twoPack === this.classList.contains('is-two-pack')) return;
      this.classList.toggle('is-two-pack', twoPack);
      const first = this.slides[0]?.dataset.mediaId;
      if (first) this.show(first);
    }

    show(mediaId) {
      const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide || !this.viewer) return;
      this.viewer.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.setActive(mediaId);
    }

    step(direction) {
      if (!this.slides.length) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(this.activeId));
      const next = this.slides[(index + direction + this.slides.length) % this.slides.length];
      this.show(next.dataset.mediaId);
    }

    setActive(mediaId) {
      this.activeId = String(mediaId);
      this.querySelectorAll('[data-dot]').forEach((dot) => dot.classList.toggle('is-active', dot.dataset.dot === String(mediaId)));
      this.thumbs.forEach((thumb) => {
        if (thumb.dataset.thumb === String(mediaId)) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-quick-add-body]');
      this.loadingHTML = this.body.innerHTML;
      this.cache = new Map();
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-quick-add]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(trigger);
      });
    }

    async open(trigger) {
      const url = trigger.dataset.quickAdd;
      this.body.innerHTML = this.loadingHTML;
      openDialog(this.dialog, trigger);
      try {
        if (!this.cache.has(url)) {
          const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-add`);
          if (!response.ok) throw new Error(response.status);
          this.cache.set(url, await response.text());
        }
        const content = parseHTML(this.cache.get(url)).querySelector('#QuickAddContent');
        if (!content) throw new Error('missing');
        this.body.replaceChildren(content);
        this.bind(content);
      } catch (error) {
        this.body.innerHTML = `<p class="quick-add__loading">${escapeHTML(this.dataset.error || '')} <a href="${escapeHTML(url)}">${escapeHTML(trigger.textContent.trim())}</a></p>`;
      }
    }

    bind(content) {
      const productUrl = content.dataset.productUrl;
      content.addEventListener('change', (event) => {
        const pill = event.target.closest('.qa__pill-input');
        if (!pill) return;
        const set = (selector, apply) => content.querySelectorAll(selector).forEach(apply);
        set('[data-qa-price]', (el) => (el.textContent = pill.dataset.price));
        set('[data-qa-compare]', (el) => {
          el.textContent = pill.dataset.compare;
          el.hidden = !pill.dataset.compare;
        });
        set('[data-qa-selected]', (el) => (el.textContent = pill.dataset.title));
        set('[data-qa-id]', (el) => (el.value = pill.value));
        const qty = Number(pill.dataset.qty) || 0;
        if (qty) set('.qa__form input[name="quantity"]', (el) => (el.value = qty));
        // Show the 2-pack photo first while 2 is picked
        if (content.querySelector('.qa__two-pack')) {
          content.classList.toggle('is-two-pack', qty === 2);
          content.querySelector('[data-qa-gallery]')?.scrollTo({ left: 0, behavior: 'smooth' });
        }
        set('[data-qa-order]', (el) => (el.href = `${productUrl}?variant=${pill.value}${qty > 1 ? `&pack=${qty}` : ''}#order`));
      });
      const gallery = content.querySelector('[data-qa-gallery]');
      const dots = Array.from(content.querySelectorAll('.qa__dots span'));
      if (!gallery || dots.length < 2) return;
      gallery.addEventListener(
        'scroll',
        () => {
          const slide = gallery.firstElementChild;
          if (!slide) return;
          const index = Math.round(Math.abs(gallery.scrollLeft) / (slide.offsetWidth + 10));
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === Math.min(index, dots.length - 1)));
        },
        { passive: true }
      );
    }
  }
  customElements.define('quick-add', QuickAdd);

  class PackSelector extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', (event) => {
        if (event.target.matches('.pack__input')) this.select(event.target);
      });
      const pack = new URLSearchParams(window.location.search).get('pack');
      const preset = pack && this.querySelector(`.pack__input[data-qty="${CSS.escape(pack)}"]:not(:disabled)`);
      if (preset) {
        preset.checked = true;
        this.select(preset);
      }
    }

    select(input) {
      if (!this.section) return;
      const qty = Number(input.dataset.qty) || 1;
      this.section.querySelectorAll('[data-pack-price]').forEach((el) => (el.textContent = input.dataset.price));
      const variantInput = this.section.querySelector('[data-variant-input]');
      if (variantInput) variantInput.value = input.dataset.variant || input.value;
      const qtyInput = this.section.querySelector('[data-qty-input]');
      if (qtyInput) qtyInput.value = input.hasAttribute('data-stack') ? 1 : qty;
      // Tell other sections (Frequently bought together) which pack is picked
      document.dispatchEvent(
        new CustomEvent('pack:change', {
          detail: { qty, variant: input.dataset.variant || input.value, stack: input.hasAttribute('data-stack') },
        })
      );
      if (input.hasAttribute('data-stack')) return;
      const url = new URL(window.location.href);
      if (input.dataset.qty) {
        if (qty > 1) url.searchParams.set('pack', qty);
        else url.searchParams.delete('pack');
      } else {
        url.searchParams.set('variant', input.value);
      }
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('pack-selector', PackSelector);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-switch-stack]');
    if (!button) return;
    const input = document.querySelector('pack-selector input[data-stack]');
    if (!input) {
      if (button.dataset.url) window.location.href = button.dataset.url;
      return;
    }
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.closest('pack-selector').scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-order-whatsapp]');
    if (!button) return;
    const section = button.closest('[data-product-section]');
    const pack = selectedPack(section);
    let message = button.dataset.message || '';
    if (!pack.title || pack.title === 'Default Title') message = message.replace(' ([pack])', '');
    openWhatsApp(button.dataset.number, fill(message, { product: section?.dataset.productTitle || '', pack: pack.title, total: pack.price }));
  });

  class CodForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-cod-form]');
      this.thanks = this.querySelector('[data-cod-thanks]');
      this.section = this.closest('[data-product-section]');
      if (!this.form) return;
      this.phone = this.form.elements.phone;
      this.phoneOk = this.querySelector('[data-phone-ok]');
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.phone?.addEventListener('input', () => {
        const valid = Boolean(normalizeLebanesePhone(this.phone.value));
        if (this.phoneOk) this.phoneOk.hidden = !valid;
        if (valid) this.setError('phone', false);
      });
      this.form.addEventListener('focusout', (event) => {
        const field = event.target.name;
        if (field && event.target.value.trim()) this.validate(field);
      });
      this.form.addEventListener('input', (event) => {
        if (event.target.name && event.target.name !== 'phone' && event.target.value.trim()) this.setError(event.target.name, false);
      });
    }

    validate(field) {
      const input = this.form.elements[field];
      if (!input) return true;
      const valid = field === 'phone' ? Boolean(normalizeLebanesePhone(input.value)) : input.value.trim().length > (field === 'address' ? 3 : 1);
      this.setError(field, !valid);
      return valid;
    }

    setError(field, show) {
      const input = this.form.elements[field];
      const error = this.querySelector(`[data-error-for="${field}"]`);
      if (error) error.hidden = !show;
      if (!input) return;
      if (show) {
        input.setAttribute('aria-invalid', 'true');
        if (error) {
          error.id = error.id || `${input.id}-error`;
          input.setAttribute('aria-describedby', error.id);
        }
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    }

    submit() {
      const fields = ['name', 'phone', 'area', 'address'];
      const invalid = fields.filter((field) => !this.validate(field));
      if (invalid.length) {
        this.form.elements[invalid[0]].focus();
        return;
      }
      const data = Object.fromEntries(new FormData(this.form));
      const digits = normalizeLebanesePhone(data.phone);
      const phone = formatLebanesePhone(digits);
      const pack = selectedPack(this.section);
      const values = {
        name: data.name.trim(),
        phone,
        area: data.area,
        address: data.address.trim(),
        product: this.dataset.product,
        pack: pack.title,
        total: pack.price,
      };

      if (this.dataset.mode === 'whatsapp') {
        let message = this.dataset.message || '';
        if (!pack.title || pack.title === 'Default Title') message = message.replace(' ([pack])', '');
        openWhatsApp(this.dataset.whatsapp, fill(message, values));
        this.showThanks(values);
        return;
      }

      const [firstName, ...rest] = values.name.split(/\s+/);
      const params = new URLSearchParams({
        'checkout[shipping_address][first_name]': firstName,
        'checkout[shipping_address][last_name]': rest.join(' ') || firstName,
        'checkout[shipping_address][phone]': `+961${digits}`,
        'checkout[shipping_address][address1]': values.address,
        'checkout[shipping_address][city]': values.area,
        'checkout[shipping_address][country]': 'LB',
        'attributes[WhatsApp]': phone,
        'attributes[Area]': values.area,
      });
      const button = this.form.querySelector('[type="submit"]');
      button?.classList.add('is-loading');
      button?.setAttribute('aria-busy', 'true');
      if (this.thanks) {
        this.thanks.textContent = this.dataset.redirecting || '';
        this.thanks.hidden = false;
      }
      window.location.href = `/cart/${pack.id}:${pack.qty || 1}?${params.toString()}`;
    }

    showThanks(values) {
      if (!this.thanks) return;
      const title = document.createElement('h3');
      title.textContent = fill(this.dataset.thanksTitle, { name: values.name.split(/\s+/)[0] });
      const text = document.createElement('p');
      text.textContent = fill(this.dataset.thanks, { phone: values.phone, total: values.total });
      this.thanks.replaceChildren(title, text);
      this.form.hidden = true;
      this.thanks.hidden = false;
      this.thanks.focus();
    }
  }
  customElements.define('cod-form', CodForm);

  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-countdown-text]');
      this.cutoff = Number(this.dataset.cutoff) || 17;
      this.closed = (this.dataset.closed || '')
        .toLowerCase()
        .split(',')
        .map((day) => day.trim())
        .filter(Boolean);
      if (!this.text) return;
      this.update();
      this.timer = setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    beirutNow() {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Beirut',
          hourCycle: 'h23',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        })
          .formatToParts(new Date())
          .map((part) => [part.type, part.value])
      );
      return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second));
    }

    update() {
      const day = 24 * 60 * 60 * 1000;
      const now = this.beirutNow();
      const deadline = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), this.cutoff));
      if (now >= deadline) deadline.setTime(deadline.getTime() + day);
      const delivery = new Date(deadline.getTime() + day);
      const weekday = (date, locale = 'en-US') => date.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
      for (let i = 0; i < 7 && this.closed.includes(weekday(delivery).toLowerCase()); i += 1) {
        delivery.setTime(delivery.getTime() + day);
      }
      const daysAway = Math.round((Date.UTC(delivery.getUTCFullYear(), delivery.getUTCMonth(), delivery.getUTCDate()) - Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) / day);
      const label = daysAway === 1 ? this.dataset.tomorrow : fill(this.dataset.onDay, { day: weekday(delivery, document.documentElement.lang || 'en') });
      const minutes = Math.max(1, Math.ceil((deadline - now) / 60000));
      const time = `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
      this.text.innerHTML = fill(this.dataset.template, { time: escapeHTML(time), day: escapeHTML(label) });
      this.hidden = false;
    }
  }
  customElements.define('delivery-countdown', DeliveryCountdown);

  class StickyOrder extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;
      this.targetVisible = true;
      this.footerVisible = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === target) this.targetVisible = entry.isIntersecting;
          else this.footerVisible = entry.isIntersecting;
        });
        const show = !this.targetVisible && !this.footerVisible;
        this.classList.toggle('is-visible', show);
        this.inert = !show;
      });
      this.observer.observe(target);
      const footer = document.querySelector('.section-footer');
      if (footer) this.observer.observe(footer);
      this.querySelector('a[href^="#"]')?.addEventListener('click', (event) => {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => target.querySelector('input:not([type="hidden"]), select')?.focus({ preventScroll: true }), 600);
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  customElements.define('sticky-order', StickyOrder);

  class StackBuilder extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-row]'));
      this.addButton = this.querySelector('[data-add]');
      this.error = this.querySelector('[data-error]');
      this.min = Number(this.dataset.min) || 3;
      this.addEventListener('change', () => this.update());
      this.addButton?.addEventListener('click', () => this.add());
      this.update();
    }

    picked() {
      return this.rows.filter((row) => row.querySelector('[data-pick]')?.checked);
    }

    update() {
      const format = this.dataset.moneyFormat;
      let total = 0;
      this.rows.forEach((row) => {
        const option = row.querySelector('[data-size]')?.selectedOptions[0];
        const checked = row.querySelector('[data-pick]')?.checked;
        row.classList.toggle('is-picked', Boolean(checked));
        if (!option) return;
        const price = row.querySelector('[data-row-price]');
        if (price) price.textContent = option.dataset.priceLabel;
        if (checked) total += Number(option.dataset.price) || 0;
      });
      const count = this.picked().length;
      const countLabel = this.querySelector('[data-count-label]');
      if (countLabel) {
        const template = count === 0 ? countLabel.dataset.zero : count === 1 ? countLabel.dataset.one : countLabel.dataset.other;
        countLabel.textContent = fill(template, { count });
      }
      const totalEl = this.querySelector('[data-total]');
      if (totalEl) totalEl.textContent = formatMoney(total, format);
      const saving = this.querySelector('[data-saving]');
      if (saving) saving.hidden = count < this.min;
      const hint = this.querySelector('[data-hint]');
      if (hint) {
        hint.hidden = count >= this.min;
        hint.textContent = fill(hint.dataset.template, { remaining: this.min - count, min: this.min });
      }
      if (this.addButton) this.addButton.disabled = count === 0;
      if (this.error) this.error.hidden = true;
    }

    async add() {
      const items = this.picked().map((row) => {
        const select = row.querySelector('[data-size]');
        return { id: Number(select.value), quantity: Number(select.selectedOptions[0]?.dataset.qty) || 1 };
      });
      if (!items.length) return;
      const drawer = document.querySelector('cart-drawer');
      this.addButton.classList.add('is-loading');
      this.addButton.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: drawer ? 'cart-drawer' : undefined, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        if (!drawer) {
          window.location.href = theme.routes.cart;
          return;
        }
        drawer.renderContents(data.sections);
        drawer.open(this.addButton);
      } catch (error) {
        if (this.error) {
          this.error.textContent = (!(error instanceof TypeError) && error.message) || theme.strings.cartError;
          this.error.hidden = false;
        }
      } finally {
        this.addButton.classList.remove('is-loading');
        this.addButton.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('stack-builder', StackBuilder);

  class BoughtTogether extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-item]'));
      this.main = this.querySelector('[data-item][data-main]');
      this.addButton = this.querySelector('[data-add]');
      this.error = this.querySelector('[data-error]');
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-pick]')) this.update();
      });
      this.addButton?.addEventListener('click', () => this.add());
      this.onPack = (event) => this.setPack(event.detail);
      document.addEventListener('pack:change', this.onPack);
      // The pack may already be picked (for example ?pack=2) before this element starts
      const picked = document.querySelector('[data-product-section] .pack__input:checked');
      if (picked && !picked.hasAttribute('data-stack')) {
        this.setPack({ qty: Number(picked.dataset.qty) || 1, variant: picked.dataset.variant || picked.value });
      } else {
        this.update();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pack:change', this.onPack);
    }

    setPack({ qty, variant, stack } = {}) {
      if (!this.main || stack) return;
      this.main.dataset.qty = qty || 1;
      if (variant) this.main.dataset.variant = variant;
      const label = this.main.querySelector('[data-pack-label]');
      if (label) label.textContent = `(${qty > 1 ? label.dataset.two : label.dataset.one})`;
      this.classList.toggle('is-two-pack', Number(qty) === 2);
      this.update();
    }

    picked() {
      return this.items.filter((item) => item.querySelector('[data-pick]')?.checked);
    }

    update() {
      const format = this.dataset.moneyFormat;
      const saving = Number(this.dataset.saving) || 0;
      const min = Number(this.dataset.min) || 3;
      let full = 0;
      let packTotal = 0;
      let units = 0;
      this.items.forEach((item) => {
        const qty = Number(item.dataset.qty) || 1;
        const line = (Number(item.dataset.price) || 0) * qty;
        const price = item.querySelector('[data-item-price]');
        if (price) price.textContent = formatMoney(line, format);
        const on = Boolean(item.querySelector('[data-pick]')?.checked);
        item.classList.toggle('is-off', !on);
        this.querySelector(`[data-image-for="${item.dataset.key}"]`)?.classList.toggle('is-off', !on);
        if (!on) return;
        full += line;
        units += qty;
        packTotal += qty === 2 && item.dataset.twoPrice ? Number(item.dataset.twoPrice) : line;
      });
      // 3 or more formulas: the store's order discount replaces the 2-pack saving
      const bundle = saving > 0 && units >= min;
      const total = bundle ? full - Math.round((full * saving) / 100) : packTotal;
      const totalEl = this.querySelector('[data-total]');
      if (totalEl) totalEl.textContent = formatMoney(total, format);
      const compare = this.querySelector('[data-compare]');
      if (compare) {
        compare.hidden = total >= full;
        compare.textContent = formatMoney(full, format);
      }
      const note = this.querySelector('[data-saving-note]');
      if (note) note.hidden = !bundle;
      if (this.addButton) this.addButton.disabled = units === 0;
      if (this.error) this.error.hidden = true;
    }

    async add() {
      const items = this.picked().map((item) => ({
        id: Number(item.dataset.variant),
        quantity: Number(item.dataset.qty) || 1,
      }));
      if (!items.length) return;
      const drawer = document.querySelector('cart-drawer');
      this.addButton.classList.add('is-loading');
      this.addButton.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: drawer ? 'cart-drawer' : undefined, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        if (!drawer) {
          window.location.href = theme.routes.cart;
          return;
        }
        drawer.renderContents(data.sections);
        drawer.open(this.addButton);
      } catch (error) {
        if (this.error) {
          this.error.textContent = (!(error instanceof TypeError) && error.message) || theme.strings.cartError;
          this.error.hidden = false;
        }
      } finally {
        this.addButton.classList.remove('is-loading');
        this.addButton.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('bought-together', BoughtTogether);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || !theme.routes.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      if (!this.hasAttribute('data-dropdown')) return;
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim()) this.results.hidden = false;
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.results.hidden = true;
        this.input.focus();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (!query) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,article,page',
        'resources[limit]': '4',
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        const doc = parseHTML(await response.text());
        const results = doc.querySelector('#PredictiveSearchResults');
        this.results.innerHTML = results ? results.innerHTML : '';
        this.results.hidden = false;
      } catch (error) {
        return;
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-auto-submit]');
    if (form) form.requestSubmit();
  });

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
      this.index = 0;
      this.slides.forEach((slide, i) => {
        slide.inert = i !== 0;
      });
      if (this.slides.length < 2) return;
      const preload = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      if (document.readyState === 'complete') preload();
      else window.addEventListener('load', preload, { once: true });
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => {
          this.show(Number(dot.dataset.slideTo));
          this.restart();
        })
      );
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i < 0) return;
        this.stop();
        this.show(i);
      });
      this.canAutoplay = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.canAutoplay) return;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.show((this.index + 1) % this.slides.length), Number(this.dataset.interval) || 7000);
    }

    stop() {
      clearInterval(this.timer);
    }

    restart() {
      if (this.canAutoplay) this.start();
    }

    show(i) {
      const current = this.slides[this.index];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      current.inert = true;
      this.index = i;
      const next = this.slides[i];
      next.classList.add('is-active', 'is-entering');
      next.addEventListener('animationend', () => next.classList.remove('is-entering'), { once: true });
      next.removeAttribute('aria-hidden');
      next.inert = false;
      this.dots.forEach((dot, j) => {
        if (j === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    disconnectedCallback() {
      this.stop();
    }
  }
  customElements.define('hero-slider', HeroSlider);

  class FeaturedSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.bar = this.querySelector('[data-progress]');
      if (!this.track || !this.bar) return;
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }

    update() {
      const { scrollWidth, clientWidth, scrollLeft } = this.track;
      const scrollable = scrollWidth - clientWidth > 4;
      this.classList.toggle('is-scrollable', scrollable);
      const size = scrollable ? clientWidth / scrollWidth : 1;
      const progress = scrollable ? Math.abs(scrollLeft) / (scrollWidth - clientWidth) : 0;
      this.bar.style.setProperty('--progress-size', `${size * 100}%`);
      this.bar.style.setProperty('--progress-offset', `${(progress * (1 - size) * 100) / size}%`);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
    }
  }
  customElements.define('featured-slider', FeaturedSlider);

  const initReveal = () => {
    const items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!items.length) return;
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach((item) => observer.observe(item));
  };

  class WhatsAppSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="tel"]');
      this.error = this.querySelector('[data-signup-error]');
      this.success = this.querySelector('[data-signup-success]');
      if (!this.form || !this.input) return;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.input.addEventListener('input', () => {
        this.error.hidden = true;
        this.input.removeAttribute('aria-invalid');
      });
    }

    submit() {
      const digits = normalizeLebanesePhone(this.input.value);
      this.success.hidden = true;
      if (!digits) {
        this.error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }
      const message = (this.dataset.message || '').replace('[phone]', formatLebanesePhone(digits));
      if (this.dataset.number) {
        window.open(`https://wa.me/${this.dataset.number}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
      }
      this.success.hidden = false;
      this.form.reset();
    }
  }
  customElements.define('whatsapp-signup', WhatsAppSignup);

  const initHeaderScroll = () => {
    const wrapper = document.querySelector('.section-header');
    if (!wrapper) return;
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const mobile = window.matchMedia('(max-width: 989px)').matches;
      const locked = document.documentElement.classList.contains('scroll-lock');
      if (!mobile || locked || y < 120) {
        wrapper.classList.remove('is-hidden');
      } else if (Math.abs(y - lastY) > 6) {
        wrapper.classList.toggle('is-hidden', y > lastY);
      }
      lastY = y;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
  };

  const initWhatsAppButton = () => {
    const button = document.querySelector('.whatsapp-button');
    const footer = document.querySelector('.section-footer');
    if (!button || !footer || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => button.classList.toggle('is-hidden', entry.isIntersecting), {
      rootMargin: '0px 0px -25% 0px',
    }).observe(footer);
  };

  initReveal();
  initWhatsAppButton();
  initHeaderScroll();
  document.addEventListener('shopify:section:load', initReveal);
})();
